// HierarchicalFilter — domain → sub-category accordion.
//
// Plan: docs/designs/intel-domain-restructure.md (D2, task T24).
//
// Used by the intel filter rail to pick conflict / disaster / trade sub-
// categories. v1 ships in the rail; later it replaces flat enum facets
// elsewhere too (Agency tree, NAICS hierarchy).
//
// Selection model: a flat ReadonlySet<string> of sub-category keys. Domain
// rows derive selected / indeterminate state from how many of their sub-
// cats are in the set. Toggling the domain checkbox bulk-selects /
// bulk-clears all sub-cats under it.
//
// Keyboard:
//   * ArrowDown / ArrowUp cycle through all VISIBLE rows (domains + any
//     expanded sub-cats).
//   * Space toggles checkbox on the focused row.
//   * Enter expands / collapses a domain row.
//
// A11y: root role=treegrid, each row role=row, domain rows have
// aria-expanded + aria-level=1, sub-cats aria-level=2; aria-checked on
// every checkbox; aria-selected reflects checkbox state.
//
// Pure presentational — no fetch, no URL, no state beyond expansion +
// focus. Counts and labels come in via props (host translates).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';

export interface HierarchicalSubcategoryOption {
  key: string;
  label: string;
  count: number;
  /** When true, the row is rendered for completeness but checkbox + click
   *  are disabled, showing "—" instead of "0" so the user can tell "no data
   *  yet" from "zero results for current filter".
   *
   *  Platform affordance (Iron Rule #11): this is a GENERAL capability of the
   *  shared rail — any EntityScreen surface may mark a taxonomy/facet row as
   *  coming-soon. It is intentionally retained even though Intel no longer
   *  emits disabled rows (Intel switched to "collapse to real capability" —
   *  it hides unbacked subcats and surfaces their count at the domain level;
   *  see packages/contracts SUBCATEGORY_VISIBLE_IN_RAIL). Do not remove
   *  without a consuming surface audit. */
  disabled?: boolean;
}

export interface HierarchicalDomainOption {
  key: string;
  label: string;
  count: number;
  subcategories: HierarchicalSubcategoryOption[];
}

export interface HierarchicalFilterProps {
  /** Domains in display order. */
  domains: HierarchicalDomainOption[];
  /** Currently selected sub-category keys. */
  value: ReadonlySet<string>;
  /** Receives a new ReadonlySet on each change. */
  onChange: (next: ReadonlySet<string>) => void;
  /** "Clear all" label (i18n by host). */
  clearAllLabel?: string;
  /** Selection-summary formatter, e.g. `(n) => `${n} selected``. */
  selectionSummary?: (count: number) => string;
  /** Default expanded domain keys. Defaults to none expanded. */
  defaultExpanded?: ReadonlySet<string>;
}

type Row =
  | { kind: 'domain'; domain: HierarchicalDomainOption; expanded: boolean }
  | { kind: 'sub'; domain: HierarchicalDomainOption; sub: HierarchicalSubcategoryOption };

export function HierarchicalFilter({
  domains,
  value,
  onChange,
  clearAllLabel = 'Clear all',
  selectionSummary = (n) => `${n} selected`,
  defaultExpanded,
}: HierarchicalFilterProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded ?? []));
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Flatten domains + expanded children into a linear row list — drives
  // keyboard navigation and rendering.
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const d of domains) {
      const isExpanded = expanded.has(d.key);
      out.push({ kind: 'domain', domain: d, expanded: isExpanded });
      if (isExpanded) {
        for (const s of d.subcategories) {
          out.push({ kind: 'sub', domain: d, sub: s });
        }
      }
    }
    return out;
  }, [domains, expanded]);

  const rowId = (r: Row) =>
    r.kind === 'domain' ? `d:${r.domain.key}` : `s:${r.domain.key}:${r.sub.key}`;

  // Map domain key → indicators. Counts derive from ENABLED subcats only —
  // disabled rows (no backing wire signal yet) can't be selected, so they
  // don't participate in the "all selected" / "indeterminate" calculation.
  const domainStateMap = useMemo(() => {
    const m = new Map<string, { selected: boolean; indeterminate: boolean }>();
    for (const d of domains) {
      const enabledKeys = d.subcategories.filter((s) => !s.disabled).map((s) => s.key);
      const selectedCount = enabledKeys.filter((k) => value.has(k)).length;
      m.set(d.key, {
        selected: enabledKeys.length > 0 && selectedCount === enabledKeys.length,
        indeterminate: selectedCount > 0 && selectedCount < enabledKeys.length,
      });
    }
    return m;
  }, [domains, value]);

  const totalSelected = value.size;

  const toggleExpand = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSub = useCallback(
    (sub: HierarchicalSubcategoryOption) => {
      if (sub.disabled) return;
      const next = new Set(value);
      if (next.has(sub.key)) next.delete(sub.key);
      else next.add(sub.key);
      onChange(next);
    },
    [value, onChange],
  );

  const toggleDomain = useCallback(
    (d: HierarchicalDomainOption) => {
      // Domain bulk-toggle only acts on the enabled subcats; disabled rows
      // cannot be selected programmatically either.
      const subKeys = d.subcategories.filter((s) => !s.disabled).map((s) => s.key);
      if (subKeys.length === 0) return;
      const next = new Set(value);
      const allEnabledSelected = subKeys.every((k) => value.has(k));
      if (allEnabledSelected) {
        for (const k of subKeys) next.delete(k);
      } else {
        for (const k of subKeys) next.add(k);
      }
      onChange(next);
    },
    [value, onChange],
  );

  const clearAll = useCallback(() => onChange(new Set()), [onChange]);

  // Keyboard navigation
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (rows.length === 0) return;
    const currentIdx = focusedRowId ? rows.findIndex((r) => rowId(r) === focusedRowId) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIdx < 0 ? 0 : Math.min(rows.length - 1, currentIdx + 1);
      setFocusedRowId(rowId(rows[next]!));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = currentIdx <= 0 ? 0 : currentIdx - 1;
      setFocusedRowId(rowId(rows[next]!));
      return;
    }
    if (e.key === ' ' || e.key === 'Spacebar') {
      if (currentIdx < 0) return;
      e.preventDefault();
      const r = rows[currentIdx]!;
      if (r.kind === 'domain') toggleDomain(r.domain);
      else toggleSub(r.sub);
      return;
    }
    if (e.key === 'Enter') {
      if (currentIdx < 0) return;
      const r = rows[currentIdx]!;
      if (r.kind === 'domain') {
        e.preventDefault();
        toggleExpand(r.domain.key);
      }
    }
  };

  // Focus the row DOM node when focusedRowId changes so SR + sighted users
  // see the focus ring move.
  useEffect(() => {
    if (!focusedRowId || !rootRef.current) return;
    const el = rootRef.current.querySelector<HTMLDivElement>(
      `[data-row-id="${cssEscape(focusedRowId)}"]`,
    );
    el?.focus();
  }, [focusedRowId]);

  return (
    <div
      ref={rootRef}
      role="treegrid"
      aria-label="hierarchical filter"
      data-component="HierarchicalFilter"
      onKeyDown={onKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--mono)',
        fontSize: 12,
      }}
    >
      <div
        data-strip="header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          borderBottom: '1px solid var(--bd)',
          color: 'var(--fg-3, #8a8a8a)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span data-summary>{selectionSummary(totalSelected)}</span>
        {totalSelected > 0 ? (
          <button
            type="button"
            data-clear-all
            onClick={clearAll}
            style={{
              background: 'transparent',
              border: '1px solid var(--bd)',
              color: 'var(--fg-2, #b8b8b8)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              padding: '2px 6px',
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            {clearAllLabel}
          </button>
        ) : null}
      </div>

      {domains.map((d) => {
        const isExpanded = expanded.has(d.key);
        const dState = domainStateMap.get(d.key) ?? { selected: false, indeterminate: false };
        const domId = `d:${d.key}`;
        const domFocused = focusedRowId === domId;
        return (
          <div key={d.key}>
            <div
              role="row"
              tabIndex={domFocused ? 0 : -1}
              data-row-id={domId}
              data-kind="domain"
              aria-level={1}
              aria-expanded={isExpanded}
              aria-selected={dState.selected}
              onFocus={() => setFocusedRowId(domId)}
              onClick={() => toggleExpand(d.key)}
              style={rowStyle(domFocused)}
            >
              <span aria-hidden="true" style={{ width: 14, color: 'var(--fg-3, #8a8a8a)' }}>
                {isExpanded ? '▾' : '▸'}
              </span>
              <input
                type="checkbox"
                aria-checked={dState.indeterminate ? 'mixed' : dState.selected}
                aria-label={d.label}
                checked={dState.selected}
                ref={(el) => {
                  if (el) el.indeterminate = dState.indeterminate;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={() => toggleDomain(d)}
                style={{ margin: 0 }}
              />
              <span data-label style={labelStyle(d.count === 0)}>
                {d.label}
              </span>
              <span data-count style={countStyle(d.count === 0)}>
                {d.count}
              </span>
            </div>
            {isExpanded
              ? d.subcategories.map((s) => {
                  const subRowId = `s:${d.key}:${s.key}`;
                  const subFocused = focusedRowId === subRowId;
                  const selected = value.has(s.key);
                  const isDisabled = s.disabled === true;
                  return (
                    <div
                      key={s.key}
                      role="row"
                      tabIndex={subFocused ? 0 : -1}
                      data-row-id={subRowId}
                      data-kind="sub"
                      data-disabled={isDisabled || undefined}
                      aria-level={2}
                      aria-selected={selected}
                      aria-disabled={isDisabled || undefined}
                      onFocus={() => setFocusedRowId(subRowId)}
                      onClick={() => toggleSub(s)}
                      style={subRowStyle(subFocused, isDisabled)}
                      title={isDisabled ? 'No signals classified yet' : undefined}
                    >
                      <span aria-hidden="true" style={{ width: 14 }} />
                      <input
                        type="checkbox"
                        aria-checked={selected}
                        aria-label={s.label}
                        checked={selected}
                        disabled={isDisabled}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSub(s)}
                        style={{ margin: 0, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                      />
                      <span data-label style={labelStyle(s.count === 0, true, isDisabled)}>
                        {s.label}
                      </span>
                      <span data-count style={countStyle(s.count === 0, isDisabled)}>
                        {isDisabled ? '—' : s.count}
                      </span>
                    </div>
                  );
                })
              : null}
          </div>
        );
      })}
    </div>
  );
}

function rowStyle(focused: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    cursor: 'pointer',
    background: focused ? 'var(--bg-pane-2, rgba(255,255,255,0.04))' : 'transparent',
    outline: 'none',
    minHeight: 22,
  };
}

function subRowStyle(focused: boolean, disabled = false): CSSProperties {
  return {
    ...rowStyle(focused),
    padding: '4px 10px 4px 28px',
    fontSize: 11,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  };
}

function labelStyle(zero: boolean, indent = false, disabled = false): CSSProperties {
  return {
    flex: 1,
    color: disabled
      ? 'var(--fg-3, #8a8a8a)'
      : zero
        ? 'var(--fg-2, #b8b8b8)'
        : 'var(--fg-1, #d4d4d4)',
    marginLeft: indent ? 0 : 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

function countStyle(zero: boolean, disabled = false): CSSProperties {
  return {
    color: disabled
      ? 'var(--fg-3, #8a8a8a)'
      : zero
        ? 'var(--fg-3, #8a8a8a)'
        : 'var(--fg-2, #b8b8b8)',
    fontSize: 10,
    fontVariantNumeric: 'tabular-nums',
    minWidth: 32,
    textAlign: 'right',
    flexShrink: 0,
  };
}

// Minimal CSS.escape polyfill (jsdom has it but Node typecheck doesn't import dom lib here)
function cssEscape(s: string): string {
  return s.replace(/([!"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g, '\\$1');
}
